import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { login } from '@/utils/auth';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import AdminAvatarMenu from '@/components/Avatars/AdminAvatarMenu';
import LevelBadge from '@/components/Levels/LevelBadge';
import Login from '@/components/Login/Login';
import CoinflipBigCoin from './CoinflipBigCoin';
import SquareTimerRing from '@/components/Coinflip/Game/SquareTimerRing';
import { coinflipStore } from '@/stores/coinflip.store';
import { modalStore } from '@/stores/modal.store';
import useUserStore from '@/stores/user.store';
import { playSound } from '@/utils/playSound';
import { notify } from '@/utils/notify';
import { requireTradeLink } from '@/utils/requireTradeLink';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { itemsValue } from '@/utils/itemInfo';
import { IGameItem } from '@/utils/types/Items';
import { IUser } from '@/utils/types/User';
import { ECoinflipSide, ICoinflipGame } from '@/utils/types/ICoinflipGame';
import { rarityColor } from '@/utils/rarity';
import { iconBackgroundStyle } from '@/utils/iconBackground';
import { getLevelColor } from '@/utils/colors';
import { syncedNow } from '@/utils/timesync';
import { useSyncedCountdown } from '@/utils/hooks/use-synced-countdown';

type Phase = 'idle' | 'flipping' | 'won' | 'settled';

interface Props {
    id?: number;
    game?: ICoinflipGame;
}

const PlayerSide = ({
    user,
    skins,
    side,
    empty,
    gameId,
    onJoin,
    isAuthed,
    pending,
    pendingSeconds,
    pendingSource,
}: {
    user?: IUser;
    skins: IGameItem[];
    side: 'left' | 'right';
    empty?: boolean;
    gameId?: number;
    onJoin?: () => void;
    isAuthed?: boolean;
    pending?: boolean;
    pendingSeconds?: number;
    pendingSource?: 'STEAM' | 'BOT';
}) => {
    const reverse = side === 'right';
    const value = itemsValue(skins);
    const lvl = user ? Math.floor((user.experience ?? 0) / 100) : 0;

    return (
        <div className={`flex items-center gap-3 ${reverse ? 'flex-row-reverse' : ''}`}>
            <div className={`flex flex-col ${reverse ? 'items-end' : ''}`}>
                <div className="text-sm font-semibold text-text_main truncate max-w-[220px]">
                    {empty ? 'Waiting for player' : user?.name}
                </div>
                {empty && onJoin && (
                    isAuthed === false ? (
                        <span className="mt-2 inline-block">
                            <Login className="px-3 py-1.5 text-[11px]" />
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={onJoin}
                            className={
                                'mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md ' +
                                'text-[11px] font-bold tracking-[0.08em] text-[#08131c] cursor-pointer ' +
                                'hover:-translate-y-[1px] hover:brightness-[1.06] transition-[transform,filter]'
                            }
                            style={{
                                background: 'linear-gradient(180deg, #5cea9d, #2bb76b)',
                                boxShadow:
                                    '0 0 22px rgba(70,224,138,0.5), 0 6px 16px rgba(70,224,138,0.32), inset 0 1px 0 rgba(255,255,255,0.4)',
                            }}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="w-3 h-3"
                            >
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            JOIN
                        </button>
                    )
                )}
                {!empty && (
                    <span className="mt-1 inline-block">
                        <LevelBadge level={lvl} color={getLevelColor(lvl)} upper={false} />
                    </span>
                )}
                {!empty && !pending && skins.length > 0 && (
                    <div className="text-xs text-text_3 mt-1 tabular-nums">
                        ${value.toFixed(2)} · {skins.length} items
                    </div>
                )}
                {!empty && pending && (
                    <div className="text-xs text-text_3 mt-1 tabular-nums">
                        {pendingSource === 'BOT' ? 'Joining' : 'Waiting for trade'} ·{' '}
                        <b className="text-accent font-bold">{pendingSeconds ?? 0}s</b>
                    </div>
                )}
            </div>
            <div className="relative w-20 h-20 max-md:w-14 max-md:h-14 shrink-0">
                {empty ? (
                    <button
                        type="button"
                        onClick={onJoin}
                        disabled={!onJoin}
                        className={
                            'w-full h-full rounded-[28%] grid place-items-center text-[28px] max-md:text-[20px] text-p2 font-light bg-transparent ' +
                            (onJoin
                                ? 'cursor-pointer transition-[transform,box-shadow,background] hover:scale-[1.03] hover:bg-p2/[.08] hover:shadow-[0_0_24px_-4px_rgba(70,224,138,0.6)]'
                                : 'cursor-default')
                        }
                        style={{
                            border: '3px dashed rgba(70,224,138,0.6)',
                        }}
                        aria-label="Join coinflip"
                    >
                        +
                    </button>
                ) : (
                    <>
                        <AdminAvatarMenu
                            userId={user!.id}
                            userName={user!.name}
                            gameType="coinflip"
                            gameId={gameId}
                            position={side === 'left' ? 'right' : 'left'}
                        >
                            <AbstractAvatar
                                seed={user!.name || String(user!.id)}
                                src={user?.avatar}
                                size={80}
                                square
                                className="!w-full !h-full max-md:!w-14 max-md:!h-14"
                            />
                        </AdminAvatarMenu>
                        <span
                            className="absolute -inset-1 rounded-[30%] pointer-events-none"
                            style={{
                                border: `3px solid ${getLevelColor(lvl)}`,
                                boxShadow: `0 0 20px -4px ${getLevelColor(lvl)}`,
                            }}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const SkinCell = ({ skin }: { skin: IGameItem }) => {
    const rarity = rarityColor(skin.colorHex);
    return (
        <div
            data-tip="1"
            data-name={skin.name}
            data-price={skin.priceUsd}
            data-rarity={rarity}
            data-image={skin.icon}
            data-icon-background={skin.iconBackground ?? ''}
            style={{ ['--rarity' as string]: rarity } as React.CSSProperties}
            className="relative bg-layer_2 border border-stroke rounded-fl px-2 pt-2.5 pb-2 flex flex-col items-center cursor-pointer transition-all hover:-translate-y-0.5 hover:bg-layer_3 hover:[border-color:var(--rarity)] hover:shadow-[0_8px_18px_rgba(0,0,0,0.35)]"
        >
            <span
                className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full pointer-events-none"
                style={{ background: rarity, boxShadow: `0 0 6px ${rarity}` }}
            />
            {skin.icon ? (
                <img
                    src={skin.icon}
                    alt=""
                    className="w-14 h-14 max-md:w-10 max-md:h-10 object-contain mb-1"
                    style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))', ...iconBackgroundStyle(skin.iconBackground) }}
                    draggable={false}
                />
            ) : (
                <div className="w-14 h-14 max-md:w-10 max-md:h-10 bg-layer_3 rounded mb-1" />
            )}
            <div className="text-sm max-md:text-[12px] font-bold text-accent_5 tabular-nums">
                ${skin.priceUsd.toFixed(2)}
            </div>
            <div className="text-[11px] max-md:text-[9.5px] text-text_3 text-center truncate w-full mt-0.5">
                {skin.name}
            </div>
        </div>
    );
};

const ViewCoinflip = ({ id, game: passedGame }: Props) => {
    const winAudio = useRef<HTMLAudioElement | null>(null);
    const betAudio = useRef<HTMLAudioElement | null>(null);

    const { activeGames, games } = coinflipStore((state) => state);
    const sounds = modalStore((state) => state.sounds);
    const { setTitle, setComponent, setData, toggleModal } = modalStore((state) => state);
    const { user, isAuthed } = useUserStore((state) => state);

    const liveGame = useMemo(() => {
        if (passedGame) return passedGame;
        if (id == null) return undefined;
        return activeGames().find((g) => g?.id === id) ?? games.get(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, passedGame, games]);

    const [phase, setPhase] = useState<Phase>('idle');
    const countdown = useSyncedCountdown(liveGame?.endsAt ?? null, 15, 'floor');
    const pendingCountdown = useSyncedCountdown(
        liveGame?.pendingJoin?.expiresAt ?? null,
        120,
        'floor'
    );

    const pendingUser = liveGame?.pendingJoin?.user as IUser | undefined;
    const displaySecondUser = liveGame?.secondUser ?? pendingUser;

    const leftUser =
        liveGame?.firstSide === ECoinflipSide.FIRST ? liveGame?.firstUser : displaySecondUser;
    const rightUser =
        liveGame?.firstSide === ECoinflipSide.FIRST ? displaySecondUser : liveGame?.firstUser;

    const leftSkins =
        liveGame?.firstSide === ECoinflipSide.FIRST ? liveGame?.firstItems : liveGame?.secondItems;
    const rightSkins =
        liveGame?.firstSide === ECoinflipSide.FIRST ? liveGame?.secondItems : liveGame?.firstItems;

    const totalValue = liveGame ? itemsValue([...liveGame.firstItems, ...liveGame.secondItems]) : 0;

    const winnerSide = liveGame?.winnerSide;
    const winnerUser = winnerSide
        ? winnerSide === ECoinflipSide.FIRST
            ? leftUser
            : rightUser
        : undefined;

    // Phase machine driven by backend signals
    useEffect(() => {
        if (!liveGame) return;
        if (passedGame || liveGame.winnerSide) {
            // History or already finished — show settled coin immediately
            setPhase('flipping');
            const t1 = window.setTimeout(() => {
                setPhase('won');
                playSound(winAudio, sounds);
            }, 400);
            return () => window.clearTimeout(t1);
        }
        setPhase('idle');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveGame?.id, !!passedGame]);

    // When backend sends winnerSide on a live game — start the big flip,
    // and schedule the "won" phase exactly at revealAt.
    useEffect(() => {
        if (!liveGame || !winnerSide || passedGame) return;
        // Once the result is shown, don't let a later store refresh (history
        // enrichment swaps in revealAt=null) restart the flip.
        if (phase === 'won' || phase === 'settled') return;
        setPhase('flipping');
        // If the backend gave a reveal timestamp, land exactly on it (synced
        // across clients); otherwise (finish signalled without revealAt) play a
        // short flip so the result still animates promptly.
        const revealAt = liveGame.revealAt ?? syncedNow() + 2800;
        const delay = Math.max(0, revealAt - syncedNow());
        const t = window.setTimeout(() => {
            setPhase('won');
            playSound(winAudio, sounds);
        }, delay);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [winnerSide, liveGame?.revealAt]);

    // Bet sound when second user joins
    useEffect(() => {
        if (!liveGame || !liveGame.secondUser || winnerSide || passedGame) return;
        playSound(betAudio, sounds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveGame?.secondUser?.id]);

    // 2s after the win lands, dismiss the big winner overlay (coin + banner /
    // nick / amount) and reveal the full two-player view, leaving just the
    // winner's avatar in the centre.
    useEffect(() => {
        if (phase !== 'won') return;
        const t = window.setTimeout(() => setPhase('settled'), 2000);
        return () => window.clearTimeout(t);
    }, [phase]);

    if (!liveGame) return null;

    const hasOpponent = !!liveGame.secondUser;
    const isReserved = !!liveGame.pendingJoin;
    const isFading = phase === 'flipping' || phase === 'won';
    const isOwner = liveGame.firstUser?.id === user.id;
    const canJoin =
        !passedGame && !winnerSide && !hasOpponent && !isReserved && !isOwner && phase === 'idle';

    // Once the result is revealed, fade the LOSER's whole side to grayscale.
    const settled = phase === 'settled';
    const winnerIsLeft = !!winnerUser && !!leftUser && winnerUser.id === leftUser.id;
    const grayLeft = settled && !!winnerSide && !winnerIsLeft;
    const grayRight = settled && !!winnerSide && winnerIsLeft;
    const loserStyle = (gray: boolean): React.CSSProperties => ({
        filter: gray ? 'grayscale(1) brightness(0.7)' : 'none',
        opacity: gray ? 0.5 : 1,
        transition: 'filter 0.45s ease, opacity 0.45s ease',
    });

    function join() {
        if (!liveGame) return;
        if (!isAuthed) {
            login();
            return;
        }
        if (!user.tradeToken) {
            requireTradeLink();
            return;
        }
        const gameId = liveGame.id;
        setTitle('MAKE DEPOSIT');
        setComponent('Make deposit');
        setData({
            siteFunc: () => {
                setTitle(`JOIN COINFLIP #${gameId}`);
                setComponent('Join coinflip');
                setData({ id: gameId });
            },
            steamFunc: () => {
                setTitle(`JOIN COINFLIP #${gameId}`);
                setComponent('Join steam coinflip');
                setData({ id: gameId });
            },
        });
        toggleModal(true);
    }

    return (
        <>
            <CoinflipBigCoin
                phase={phase}
                p1={leftUser}
                p2={rightUser}
                winnerSide={winnerSide}
                winnerNick={winnerUser?.name ?? '—'}
                amount={totalValue}
                flipStartAt={liveGame.flipStartAt}
                revealAt={liveGame.revealAt}
            />

            <div
                className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 max-md:gap-2 mb-7 max-md:mb-4 pt-3 transition-opacity"
                style={{ opacity: isFading ? 0.12 : 1, transitionDuration: '0.4s' }}
            >
                <div className="flex justify-end" style={loserStyle(grayLeft)}>
                    {leftUser ? (
                        <PlayerSide
                            user={leftUser}
                            skins={leftSkins ?? []}
                            side="left"
                            gameId={liveGame.id}
                            pending={isReserved && leftUser?.id === pendingUser?.id}
                            pendingSeconds={pendingCountdown}
                            pendingSource={liveGame.pendingJoin?.source}
                        />
                    ) : (
                        <PlayerSide
                            skins={[]}
                            side="left"
                            empty
                            onJoin={canJoin ? join : undefined}
                            isAuthed={isAuthed}
                        />
                    )}
                </div>

                <div className="flex flex-col items-center gap-3 min-w-[80px] max-md:min-w-0">
                    <div className="relative">
                        {phase === 'settled' && winnerUser ? (
                            <div
                                className="relative w-16 h-16 max-md:w-12 max-md:h-12"
                                style={{
                                    animation:
                                        'wnAvaPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both',
                                }}
                            >
                                <span
                                    className="absolute -inset-1.5 rounded-[30%] blur-[8px] opacity-60 -z-[1]"
                                    style={{ background: '#ffc24d' }}
                                />
                                <AbstractAvatar
                                    seed={winnerUser.name || String(winnerUser.id)}
                                    src={winnerUser.avatar}
                                    size={64}
                                    square
                                    className="!w-full !h-full max-md:!w-12 max-md:!h-12"
                                />
                                <span
                                    className="absolute -inset-1 rounded-[30%] pointer-events-none"
                                    style={{
                                        border: '2.5px solid #ffc24d',
                                        boxShadow: '0 0 20px -3px rgba(255,194,77,0.8)',
                                    }}
                                />
                                <span
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[15px] leading-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                                    aria-hidden
                                >
                                    👑
                                </span>
                            </div>
                        ) : (
                            <div className="relative w-14 h-14 max-md:w-11 max-md:h-11">
                                <span
                                    className="absolute -inset-1 rounded-[30%] blur-[8px] opacity-40 -z-[1]"
                                    style={{
                                        background:
                                            'conic-gradient(from 0deg, var(--p1), #ffc24d, var(--p2), var(--p1))',
                                    }}
                                />
                                {hasOpponent && !winnerSide && (
                                    <SquareTimerRing
                                        seconds={countdown}
                                        max={15}
                                        size={56}
                                        stroke={3}
                                        radius={16}
                                    />
                                )}
                                <div className="font-chat-nick absolute inset-0 grid place-items-center rounded-[28%] bg-layer_2 border border-stroke text-[12px] max-md:text-[10px] font-extrabold tracking-[0.06em] text-white tabular-nums">
                                    {hasOpponent && !winnerSide ? countdown : 'VS'}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-start" style={loserStyle(grayRight)}>
                    {rightUser ? (
                        <PlayerSide
                            user={rightUser}
                            skins={rightSkins ?? []}
                            side="right"
                            gameId={liveGame.id}
                            pending={isReserved && rightUser?.id === pendingUser?.id}
                            pendingSeconds={pendingCountdown}
                            pendingSource={liveGame.pendingJoin?.source}
                        />
                    ) : (
                        <PlayerSide
                            skins={[]}
                            side="right"
                            empty
                            onJoin={canJoin ? join : undefined}
                            isAuthed={isAuthed}
                        />
                    )}
                </div>
            </div>

            <div
                className="grid grid-cols-2 gap-20 max-md:gap-3 mb-4 px-1 transition-opacity"
                style={{ opacity: isFading ? 0.12 : 1, transitionDuration: '0.4s' }}
            >
                {(leftSkins?.length ?? 0) > 0 && (
                    <div
                        className="flex gap-2 justify-end max-md:flex-wrap"
                        style={loserStyle(grayLeft)}
                    >
                        <span className="px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-xs text-text_3 font-medium">
                            Deposited skins:{' '}
                            <b className="text-accent_5 font-bold ml-1 tabular-nums">
                                {leftSkins?.length ?? 0}
                            </b>
                        </span>
                        <span className="px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-xs text-text_3 font-medium">
                            Value:{' '}
                            <b className="text-accent_5 font-bold ml-1 tabular-nums">
                                ${itemsValue(leftSkins ?? []).toFixed(2)}
                            </b>
                        </span>
                    </div>
                )}
                {(rightSkins?.length ?? 0) > 0 && (
                    <div className="flex gap-2 justify-start max-md:flex-wrap" style={loserStyle(grayRight)}>
                        <span className="px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-xs text-text_3 font-medium">
                            Deposited skins:{' '}
                            <b className="text-accent_5 font-bold ml-1 tabular-nums">
                                {rightSkins?.length ?? 0}
                            </b>
                        </span>
                        <span className="px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-xs text-text_3 font-medium">
                            Value:{' '}
                            <b className="text-accent_5 font-bold ml-1 tabular-nums">
                                ${itemsValue(rightSkins ?? []).toFixed(2)}
                            </b>
                        </span>
                    </div>
                )}
            </div>

            <div
                className="grid grid-cols-2 gap-20 max-md:gap-3 mb-5 px-1 min-h-[160px] max-md:min-h-[120px] transition-opacity"
                style={{ opacity: isFading ? 0.12 : 1, transitionDuration: '0.4s' }}
            >
                <div
                    className="grid gap-2 content-start [grid-template-columns:repeat(auto-fill,minmax(96px,1fr))] max-md:[grid-template-columns:repeat(auto-fill,minmax(72px,1fr))]"
                    style={{ direction: 'rtl', ...loserStyle(grayLeft) }}
                >
                    {(leftSkins?.length ?? 0) > 0 ? (
                        (leftSkins ?? []).map((s, i) => (
                            <div key={i} style={{ direction: 'ltr' }}>
                                <SkinCell skin={s} />
                            </div>
                        ))
                    ) : isReserved && leftUser?.id === pendingUser?.id ? (
                        <div
                            className="col-span-full min-h-[120px] flex items-center justify-center gap-3 text-xs text-text_3"
                            style={{ direction: 'ltr' }}
                            role="status"
                        >
                            <span
                                className="w-5 h-5 rounded-full border-2 border-accent/25 border-t-accent animate-spin"
                                aria-hidden="true"
                            />
                            <span>Waiting for player's wager...</span>
                        </div>
                    ) : null}
                </div>
                <div
                    className="grid gap-2 content-start [grid-template-columns:repeat(auto-fill,minmax(96px,1fr))] max-md:[grid-template-columns:repeat(auto-fill,minmax(72px,1fr))]"
                    style={loserStyle(grayRight)}
                >
                    {(rightSkins?.length ?? 0) > 0 ? (
                        (rightSkins ?? []).map((s, i) => <SkinCell key={i} skin={s} />)
                    ) : isReserved && rightUser?.id === pendingUser?.id ? (
                        <div
                            className="col-span-full min-h-[120px] flex items-center justify-center gap-3 text-xs text-text_3"
                            role="status"
                        >
                            <span
                                className="w-5 h-5 rounded-full border-2 border-accent/25 border-t-accent animate-spin"
                                aria-hidden="true"
                            />
                            <span>Waiting for player's wager...</span>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="flex items-center gap-2.5 max-md:flex-wrap mt-auto pt-3.5 border-t border-stroke">
                <span className="px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-xs text-text_3">
                    Secret{' '}
                    <b className="text-accent_5 font-semibold ml-1 font-mono text-[11px]">
                        {liveGame.secret ?? '—'}
                    </b>
                </span>
                <span className="px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-xs text-text_3 truncate max-w-[400px] max-md:max-w-[200px] max-md:flex-1">
                    Hash{' '}
                    <b className="text-accent_5 font-semibold ml-1 font-mono text-[11px]">
                        {liveGame.hash}
                    </b>
                </span>
                <Link
                    href={`/fairplay?game=coinflip&hash=${liveGame.hash}&secret=${liveGame.secret ?? ''}`}
                    className="ml-auto max-md:ml-0 max-md:w-full max-md:text-center text-[11px] px-4 py-2 rounded-md bg-accent/[.12] border border-accent/30 text-accent font-bold tracking-[0.08em] hover:bg-accent/20"
                >
                    FAIR PLAY
                </Link>
            </div>

            <audio ref={betAudio} src="/audio/coinflip-bet.mp3" />
            <audio ref={winAudio} src="/audio/win.mp3" />
        </>
    );
};

export default ViewCoinflip;
